import classNames from 'classnames'
import React from 'react'

import { Card } from '../../card/card.js'

export type OptionCardInputAttributes = Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    'checked' | 'defaultChecked' | 'onChange' | 'disabled' | 'value'
>

export interface OptionCardProps {
    children: React.ReactNode
    id: string
    labelText: string
    inputType?: 'radio' | 'checkbox'
    value?: string
    input?: OptionCardInputAttributes
    checked?: boolean
    isDisabled?: boolean
    isInvalid?: boolean
    className?: string
    type?: 'minimal' | 'simple'
    width?: 'sm-6' | 'sm-12'
    onChange?: (checked: boolean) => void
}

export const OptionCard = (props: OptionCardProps): JSX.Element => {
    const {
        checked,
        children,
        isDisabled,
        id,
        input,
        inputType = 'checkbox',
        isInvalid,
        labelText,
        value = 'on',
    } = props

    const inputRef: React.RefObject<HTMLInputElement> = React.createRef<HTMLInputElement>()

    const onClick = () => onChange()

    const className: string = classNames(
        'option-card',
        `option-card--input-${inputType}`,
        { 'option-card--type-minimal': props.type === 'minimal' },
        { 'option-card--type-simple': props.type === 'simple' },
        props.className,
    )

    const onChange = (): void => {
        if (inputRef.current) {
            const isChecked: boolean = inputRef.current.checked
            const nextChecked = inputType === 'radio' ? true : !isChecked

            if (isDisabled || (inputType === 'radio' && isChecked)) {
                return
            }

            if (props.onChange) {
                props.onChange(nextChecked)
            }
        }
    }

    return (
        <div className={className} onClick={onClick}>
            <input
                {...input}
                value={value}
                type={inputType}
                ref={inputRef}
                onChange={onChange}
                checked={checked}
                defaultChecked={undefined}
                disabled={isDisabled}
                id={id}
                className="option-card__input"
                aria-invalid={isInvalid}
            />
            <label htmlFor={id} className="option-card__label">
                {labelText}
            </label>
            <Card className="option-card__body" hasContent={true} variant={checked ? 'secondary' : undefined}>
                {children}
            </Card>
        </div>
    )
}
